import { useSubscription } from '@apollo/client';

import { CompanyMetadataStatus } from '../../../__generated__/GQL';
import { apolloClient } from '../../../common/graphql/clients';
import useCompanyContext from '../../../common/hooks/useCompanyContext';
import { RootDocument } from '../../../Main/SecuredRoutes/graphql/root.gql';
import { ApplicationAbortedDocument } from '../graphql/subscriptions/applicationAborted.gql';
import { OnboardingModuleData } from '../machine/model';

/**
 * This hook makes sure the user is redirected to the start of the onboarding when the application is aborted.
 * Both on initial load or when the user has already an active tab, thanks to the `applicationAborted` subscription.
 */
export const useClearAbortedApplication = (
  machineEvent: () => void,
  moduleData: OnboardingModuleData,
) => {
  const companyContext = useCompanyContext();

  // Clear on initial load
  if (
    moduleData.companyData?.company.metadata.status ===
    CompanyMetadataStatus.Closed
  ) {
    machineEvent();
    companyContext.clearCompanyContext();
  }

  // Clear on subscription
  useSubscription(ApplicationAbortedDocument, {
    onData: async ({ data: subscriptionData }) => {
      if (
        companyContext.companyProfileId ===
        subscriptionData.data?.applicationAborted.applicationId
      ) {
        machineEvent();
        // We need to force a network refetch of the company and companyUsers
        // before clearing the company context, otherwise there will be an error
        // due to inconsistencies between them
        await apolloClient.query({
          fetchPolicy: 'network-only',
          query: RootDocument,
        });
        companyContext.clearCompanyContext();
      }
    },
  });
};
